<template>
  <v-row no-gutters class="fill-height flex-column">
    <v-col>
      <card-title-row class="pl-0 pt-5 pb-8" :straight-title="additionalModuleName" :has-buttons="false" />
      <sidebar-row>
        <v-row>
          <v-col>
            <base-button-toggleable
              :is-active.sync="isCoordinatesToolActive"
              @click="toggleCoordinatesInteraction"
              translation-path="map.getPointFromMap"
            />
          </v-col>
          <v-col cols="auto">
            <base-rounded-button
              icon="$csv"
              size="x-small"
              color="success"
              :disabled="!cursorPositionCoordinates.length > 0"
              @click="saveToCsv"
              :tooltip="cursorPositionCoordinates.length > 0 ? $i18n.t('sidebar.exportCoordsToCsv') : null"
            />
            <dots-menu
              :items="dotsMenuItems"
              :disabled="!cursorPositionCoordinates.length > 0"
              @delete="clearCoordinatesInteraction"
            ></dots-menu>
          </v-col>
        </v-row>
      </sidebar-row>
      <sidebar-row class="pt-6">
        <v-row>
          <v-col>
            <v-card elevation="3">
              <v-data-table
                :headers="tableHeaders"
                class="coordinates"
                hide-default-footer
                :items-per-page="-1"
                fixed-header
                hide-default-header
                :items="cursorPositionCoordinates"
              >
                <template #header="data">
                  <thead style="background-color: #fff">
                    <tr>
                      <th v-for="header in data.props.headers" :key="header.value" class="text-center">
                        {{ header.text }}
                      </th>
                    </tr>
                    <tr>
                      <th style="padding: 0 16px"></th>
                      <th v-for="(coord, index) of ['x', 'y']" :key="coord" style="padding: 0 16px">
                        <data-input
                          v-bind="coordsInputsProps"
                          v-model="inputCoords[index]"
                          style="max-width: 100px; margin: 10px auto"
                        ></data-input>
                      </th>
                      <th style="padding: 0 16px">
                        <v-btn
                          color="success"
                          text
                          icon
                          :disabled="inputCoords.some(coord => !coord)"
                          @click="addCoordinatesToList"
                        >
                          <v-icon>mdi-check</v-icon>
                        </v-btn>
                      </th>
                    </tr>
                  </thead>
                </template>
                <template #[`item.id`]="{ item }"
                  ><span style="white-space: nowrap">{{ item.id }}.</span></template
                >
                <template #[`item.actions`]="{ item, index }">
                  <td class="text-right" style="white-space: nowrap">
                    <v-icon @click="zoomToCoordinate(item.id)">mdi-magnify</v-icon>
                    <dots-menu :items="dotsMenuItems" @delete="deleteCoordinate(item.id, index)"></dots-menu>
                  </td>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </sidebar-row>
    </v-col>
  </v-row>
</template>

<script>
import { get, sync } from 'vuex-pathify';

import DotsMenu from '@/components/DotsMenu';
import SidebarRow from '@/components/SidebarRow';
import DataInput from '@/components/DataInput';
import CardTitleRow from '@/components/CardTitleRow';

export default {
  name: 'Coordinates',
  components: {
    DotsMenu,
    SidebarRow,
    DataInput,
    CardTitleRow,
  },
  data: () => ({
    inputCoords: [null, null],
    pointGeometry: null,
    dotsMenuItems: [
      {
        name: 'delete',
      },
    ],
  }),
  computed: {
    additionalModuleName: get('admin/additionalModules.COORDINATES.verbose_name'),
    coordinates: get('map/coordinates'),
    activeTool: get('tools/activeTool'),
    cursorPositionEpsg: get('map/cursorPositionEpsg'),
    isCoordinatesToolActive: sync('tools/toolStatus@isCoordinatesToolActive'),
    isCoordinateInteractionActive() {
      return this.activeTool === 'coordinates';
    },
    tableHeaders() {
      return Object.entries({
        id: '',
        x: 'X',
        y: 'Y',
        actions: '',
      }).map(([value, text]) => {
        return {
          text,
          value,
          align: 'center',
        };
      });
    },
    cursorPositionCoordinates() {
      return this.coordinates.map(coordinate => {
        const { x, y } = coordinate[this.cursorPositionEpsg];
        return { id: coordinate.id, x: x.toFixed(6), y: y.toFixed(6) };
      });
    },
    coordsInputsProps() {
      return {
        dataType: {
          name: 'Float',
        },
        dense: true,
        hideDetails: true,
      };
    },
  },
  methods: {
    addCoordinatesInteraction() {
      this.$root.$emit('coordinates-action', true);
    },
    addCoordinatesToList() {
      this.$root.$emit('addCoordinates', this.inputCoords);
      this.inputCoords = [null, null];
    },
    clearCoordinatesInteraction() {
      this.$root.$emit('coordinates-action', false);
    },
    turnOffCoordinatesInteraction() {
      this.$root.$emit('turnOffCoordinatesInteraction');
    },
    toggleCoordinatesInteraction() {
      this.isCoordinatesToolActive ? this.addCoordinatesInteraction() : this.turnOffCoordinatesInteraction();
    },
    getCsvFileContent() {
      const coordinatesPrecise = this.coordinates.map(coordinate => {
        const { x, y } = coordinate[this.cursorPositionEpsg];
        return { id: coordinate.id, x, y };
      });
      return coordinatesPrecise
        .map(coordinate =>
          Object.keys(coordinate)
            .map(key => coordinate[key])
            .join(',')
        )
        .join('\n');
    },
    getCsvBlobUrl() {
      const fileContent = this.getCsvFileContent();
      const file = new Blob([fileContent], { type: 'text/csv;charset=utf-8;' });
      return URL.createObjectURL(file);
    },
    saveToCsv() {
      this.$_saveFile(this.getCsvBlobUrl(), 'coordinates.csv');
    },
    zoomToCoordinate(id) {
      this.$root.$emit('zoomToFeature', 'coordinates', null, id);
    },
    deleteCoordinate(id, index) {
      this.$store.set('map/DELETE_CLICKED_COORDINATES!', index);
      this.$root.$emit('deleteFeature', 'coordinates', id);
    },
  },
  beforeDestroy() {
    this.clearCoordinatesInteraction();
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-data-table__empty-wrapper {
    display: none;
  }
  .v-input__slot {
    min-height: 30px !important;
    font-size: 12px !important;
  }
  td {
    font-size: 12px !important;
  }
  .v-data-table__wrapper {
    max-height: calc(100vh - 400px);
    overflow-y: auto;
    overflow-x: hidden;
  }
}
</style>
